/**
 * Pure helpers for AiAssistantDialog message-text manipulation. Extracted
 * from the dialog so they can be unit-tested in isolation and so the dialog
 * file is not a 2000-row monolith.
 *
 * The chat `Msg` type lives here too because it is the natural input/output
 * shape for these helpers (and only the dialog renders it elsewhere).
 */
import { stripWorkflowJson, stripWorkflowJsonStreaming, extractWorkflowJson } from '@/lib/aiJsonStrip';
import { formatToolResultMessage } from '@/lib/aiToolCallStream';
import type { ToolResult } from '@/lib/processMotorTools';

export type Msg = { role: 'user' | 'assistant'; content: string; error?: string };

/**
 * Extract workflow-JSON blocks from an assistant message. Delegates to
 * aiJsonStrip which handles both fenced (```json ... ```) and ofenced
 * (top-level `{...}` with workflow signature) variants — AI glömmer ibland
 * fence-wrap, vilket annars skulle lämna rå state synlig och orörbar av
 * Import-knappen.
 */
export function extractJsonBlocks(text: string): string[] {
  return extractWorkflowJson(text);
}

/** Extract `[[option]]` markers used for clickable assistant suggestions. */
export function extractOptions(text: string): string[] {
  const regex = /\[\[([^\]]+)\]\]/g;
  const results: string[] = [];
  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    results.push(match[1].trim());
  }
  return results;
}

/** Remove `[[option]]` markers from rendered text + collapse extra newlines. */
export function stripOptions(text: string): string {
  return text.replace(/\[\[[^\]]+\]\]/g, '').replace(/\n{3,}/g, '\n\n');
}

/**
 * Skrubba rå workflow-JSON från text innan rendering OCH persist. Täcker
 * både ` ```json ... ``` `-fenced och ofenced top-level `{...}`-objekt med
 * workflow-signatur. Den senare gruppen var en exploit-vektor — AI glömmer
 * ibland fence-wrap och råa state-dumps exponerades i chat-bubblan.
 */
export function stripJsonBlocks(text: string): string {
  return stripWorkflowJson(text);
}

/**
 * Streaming-aware variant of `stripJsonBlocks`. Also masks UNCLOSED
 * `{...` and ` ```json...` regions that already carry a workflow
 * signature — so the user never sees raw JSON flashing during the
 * AI's stream. Use this in the render path only; persist still uses
 * the closed-block variant.
 */
export function stripJsonBlocksStreaming(text: string): string {
  return stripWorkflowJsonStreaming(text);
}

/**
 * I-005 B4: Push a tool-call result as a new assistant message. Formatting
 * is delegated to aiToolCallStream.formatToolResultMessage (pure, testable).
 */
export function injectToolResultAsMessage(
  setMessages: React.Dispatch<React.SetStateAction<Msg[]>>,
  toolName: string,
  result: ToolResult,
): void {
  const content = formatToolResultMessage(toolName, result);
  setMessages((prev) => [...prev, { role: 'assistant', content }]);
}
